import { AlertCircle, Eye, EyeOff, Lock, Mail, UserRound } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ApiError } from '@/lib/apiClient';
import { useAuth } from '@/lib/auth';

export function SignupPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (!acceptedTerms || !acceptedPrivacy) {
      setError('You must accept the Terms of Service and Privacy Policy.');
      return;
    }

    try {
      await register.mutateAsync({
        displayName: displayName.trim(),
        email: email.trim(),
        password,
        acceptedTerms: true,
        acceptedPrivacy: true,
      });
      navigate('/', { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError) setError(caught.message);
      else setError('Unable to create your account right now. Please try again.');
    }
  };

  return (
    <div className="from-brand-50 flex min-h-screen items-center justify-center bg-gradient-to-br to-slate-50 px-4 py-12">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-slate-900">Create account</h1>
          <p className="mt-2 text-slate-600">Join Jemmi and start creating challenges</p>
        </div>

        {error && (
          <div className="mb-5 flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <Field icon={<UserRound className="h-4 w-4" />}>
            <label htmlFor="displayName" className="mb-2 block text-sm font-medium text-slate-900">
              Display name
            </label>
            <input
              id="displayName"
              required
              minLength={1}
              maxLength={40}
              autoComplete="name"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              className="focus:ring-brand-200 w-full rounded-lg border border-slate-200 py-2 pl-10 pr-3 text-sm focus:outline-none focus:ring-2"
              disabled={register.isPending}
            />
          </Field>

          <Field icon={<Mail className="h-4 w-4" />}>
            <label htmlFor="email" className="mb-2 block text-sm font-medium text-slate-900">
              Email address
            </label>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="focus:ring-brand-200 w-full rounded-lg border border-slate-200 py-2 pl-10 pr-3 text-sm focus:outline-none focus:ring-2"
              disabled={register.isPending}
            />
          </Field>

          <div>
            <label htmlFor="password" className="mb-2 block text-sm font-medium text-slate-900">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                required
                minLength={12}
                maxLength={128}
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="focus:ring-brand-200 w-full rounded-lg border border-slate-200 py-2 pl-10 pr-10 text-sm focus:outline-none focus:ring-2"
                disabled={register.isPending}
              />
              <button
                type="button"
                onClick={() => setShowPassword((value) => !value)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="mt-1 text-xs text-slate-500">Use at least 12 characters.</p>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="mb-2 block text-sm font-medium text-slate-900">
              Confirm password
            </label>
            <input
              id="confirmPassword"
              type="password"
              required
              minLength={12}
              maxLength={128}
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              className="focus:ring-brand-200 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2"
              disabled={register.isPending}
            />
          </div>

          <label className="flex items-start gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              className="mt-1"
              checked={acceptedTerms}
              onChange={(event) => setAcceptedTerms(event.target.checked)}
              disabled={register.isPending}
            />
            <span>
              I agree to the{' '}
              <Link className="text-brand-600 font-semibold" to="/terms">
                Terms of Service
              </Link>
              .
            </span>
          </label>

          <label className="flex items-start gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              className="mt-1"
              checked={acceptedPrivacy}
              onChange={(event) => setAcceptedPrivacy(event.target.checked)}
              disabled={register.isPending}
            />
            <span>
              I have read the{' '}
              <Link className="text-brand-600 font-semibold" to="/privacy">
                Privacy Policy
              </Link>
              .
            </span>
          </label>

          <button
            type="submit"
            disabled={register.isPending}
            className="bg-brand-600 hover:bg-brand-700 w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            {register.isPending ? 'Creating account…' : 'Create account'}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-slate-600">
          Already have an account?{' '}
          <Link to="/login" className="text-brand-600 hover:text-brand-700 font-semibold">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

function Field({ children, icon }: { children: React.ReactNode; icon: React.ReactNode }) {
  return (
    <div>
      {children instanceof Array ? children[0] : null}
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{icon}</span>
        {children instanceof Array ? children[1] : children}
      </div>
    </div>
  );
}
